import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Close, ContentPaste, DeleteOutline, PhotoCamera, SwapHoriz } from '@mui/icons-material';
import toast from 'react-hot-toast';
import api, { teacherModuleAPI } from '../../services/api';

function AuthImage({ src, alt, style, onClick }) {
  const [blobUrl, setBlobUrl] = useState('');
  useEffect(() => {
    if (!src) return undefined;
    let objectUrl = '';
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(src, { responseType: 'blob', timeout: 60000 });
        const blob = res.data;
        if (blob?.type && String(blob.type).includes('application/json')) return;
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setBlobUrl(objectUrl);
      } catch (_) {
        /* leave placeholder */
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);
  if (!blobUrl) {
    return (
      <Box
        onClick={onClick}
        sx={{ height: style?.height || 140, bgcolor: 'action.hover', minWidth: 80 }}
      />
    );
  }
  return <img src={blobUrl} alt={alt} style={style} onClick={onClick} />;
}

export default function SessionClassPhotos({ sessionId, canManage: canManageProp, photos: photosProp }) {
  const queryClient = useQueryClient();
  const addRef = useRef(null);
  const replaceRef = useRef(null);
  const [replaceId, setReplaceId] = useState('');
  const [openId, setOpenId] = useState('');

  const query = useQuery(
    ['session-class-photos', sessionId],
    async () => {
      const res = await teacherModuleAPI.listSessionPhotos(sessionId);
      return res.data?.data || { photos: [], canManage: false };
    },
    { enabled: !!sessionId && photosProp == null }
  );

  const photos = photosProp != null ? photosProp : query.data?.photos || [];
  const canManage = canManageProp != null ? canManageProp : !!query.data?.canManage;
  const openPhoto = photos.find((p) => p.id === openId);

  const invalidate = () => {
    queryClient.invalidateQueries(['session-class-photos', sessionId]);
    queryClient.invalidateQueries(['teacherSessionDetail', sessionId]);
    queryClient.invalidateQueries(['batch']);
  };

  const uploadMut = useMutation((formData) => teacherModuleAPI.uploadSessionPhotos(sessionId, formData), {
    onSuccess: () => {
      toast.success('Photo saved');
      invalidate();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Could not upload photo'),
  });

  const replaceMut = useMutation(
    ({ photoId, formData }) => teacherModuleAPI.replaceSessionPhoto(sessionId, photoId, formData),
    {
      onSuccess: () => {
        toast.success('Photo replaced');
        setReplaceId('');
        invalidate();
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Could not replace photo'),
    }
  );

  const deleteMut = useMutation((photoId) => teacherModuleAPI.deleteSessionPhoto(sessionId, photoId), {
    onSuccess: () => {
      toast.success('Photo removed');
      if (openId) setOpenId('');
      invalidate();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Could not delete photo'),
  });

  const onAddFiles = (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    Array.from(files).forEach((file) => fd.append('photos', file, file.name || 'screenshot.png'));
    uploadMut.mutate(fd);
  };

  const onPasteImages = (event) => {
    if (!canManage || uploadMut.isLoading) return;
    const files = Array.from(event.clipboardData?.items || [])
      .filter((item) => String(item.type || '').startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter(Boolean);
    if (!files.length) return;
    event.preventDefault();
    onAddFiles(files);
  };

  const pasteFromClipboard = async () => {
    if (!navigator.clipboard?.read) {
      toast.error('Click Class photos and press Ctrl+V to paste a screenshot');
      return;
    }
    try {
      const items = await navigator.clipboard.read();
      const files = [];
      for (const item of items) {
        const type = item.types.find((entry) => entry.startsWith('image/'));
        if (!type) continue;
        const blob = await item.getType(type);
        const ext = type.includes('png') ? 'png' : type.includes('webp') ? 'webp' : 'jpg';
        files.push(new File([blob], `screenshot.${ext}`, { type }));
      }
      if (!files.length) {
        toast.error('Copy a screenshot first, then paste it here');
        return;
      }
      onAddFiles(files);
    } catch (err) {
      if (err?.response) {
        toast.error(err.response?.data?.message || 'Could not save screenshot');
        return;
      }
      toast.error('Could not read the clipboard. Click Class photos and press Ctrl+V.');
    }
  };

  const onReplaceFile = (file) => {
    if (!file || !replaceId) return;
    const fd = new FormData();
    fd.append('photo', file);
    replaceMut.mutate({ photoId: replaceId, formData: fd });
  };

  if (!sessionId) return null;
  if (query.isLoading && photosProp == null) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  return (
    <Paper
      variant="outlined"
      tabIndex={canManage ? 0 : undefined}
      onPaste={onPasteImages}
      sx={{ p: 2, mb: 2, borderRadius: 2, outline: 'none' }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={800}>
          Class photos
        </Typography>
        {canManage ? (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentPaste />}
              onClick={pasteFromClipboard}
              disabled={uploadMut.isLoading}
            >
              Paste screenshot
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PhotoCamera />}
              onClick={() => addRef.current?.click()}
              disabled={uploadMut.isLoading}
            >
              Add photos
            </Button>
          </Stack>
        ) : null}
      </Stack>
      {canManage ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Copy a screenshot, click this box, and press Ctrl+V. You do not need to save the image first.
        </Typography>
      ) : null}
      <input
        ref={addRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,.jpg,.jpeg,.png,.webp,.heic"
        multiple
        hidden
        onChange={(e) => {
          onAddFiles(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={replaceRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,.jpg,.jpeg,.png,.webp,.heic"
        hidden
        onChange={(e) => {
          onReplaceFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      {photos.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          {canManage
            ? 'Paste a screenshot or add snapshots from this class. Families and staff who open this class can see them.'
            : 'No photos for this class yet.'}
        </Typography>
      ) : (
        <ImageList cols={photos.length === 1 ? 1 : 3} gap={8} sx={{ m: 0 }}>
          {photos.map((p) => (
            <ImageListItem key={p.id} sx={{ cursor: 'pointer', borderRadius: 1, overflow: 'hidden' }}>
              <AuthImage
                src={p.url}
                alt={p.originalName || 'Class photo'}
                style={{ height: 140, width: '100%', objectFit: 'cover' }}
                onClick={() => setOpenId(p.id)}
              />
              {canManage ? (
                <ImageListItemBar
                  actionIcon={
                    <Stack direction="row">
                      <IconButton
                        size="small"
                        sx={{ color: '#fff' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setReplaceId(p.id);
                          replaceRef.current?.click();
                        }}
                        aria-label="Replace photo"
                      >
                        <SwapHoriz fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        sx={{ color: '#fff' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('Remove this photo?')) deleteMut.mutate(p.id);
                        }}
                        aria-label="Delete photo"
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Stack>
                  }
                />
              ) : null}
            </ImageListItem>
          ))}
        </ImageList>
      )}

      <Dialog open={!!openPhoto} onClose={() => setOpenId('')} maxWidth="md">
        <Box sx={{ position: 'relative', bgcolor: '#000' }}>
          <IconButton
            onClick={() => setOpenId('')}
            sx={{ position: 'absolute', top: 4, right: 4, color: '#fff', zIndex: 1 }}
          >
            <Close />
          </IconButton>
          {openPhoto ? (
            <AuthImage
              src={openPhoto.url}
              alt={openPhoto.originalName || 'Class photo'}
              style={{ maxWidth: '100%', maxHeight: '80vh', display: 'block' }}
            />
          ) : null}
        </Box>
      </Dialog>
    </Paper>
  );
}
